/*
  FORM — Drop 001 product section.

  Owns the two interactive pieces of the design that Dawn's stock
  product-info does not cover: the flat option chips (rendered as radios)
  and the sticky buy bar that slides in once the in-page buy buttons
  scroll away.

  The gallery is not ours — it's Dawn's stock media gallery, so a colour
  change re-renders the section and swaps the markup in, exactly as
  product-info.js does. Add-to-cart likewise goes through Dawn's
  <product-form>, so the cart drawer / notification behave as elsewhere.
*/

if (!customElements.get('form-product-drop001')) {
  customElements.define(
    'form-product-drop001',
    class FormProductDrop001 extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('[data-form-pdp-variants]');
        this.variants = data ? JSON.parse(data.textContent) : [];

        this.idInput = this.querySelector('[data-form-pdp-id]');
        this.priceTarget = this.querySelector('[data-form-pdp-price]');
        this.stickyPrice = this.querySelector('[data-form-pdp-sticky-price]');
        this.submitButtons = Array.from(this.querySelectorAll('[data-form-pdp-submit]'));
        this.paymentButton = this.querySelector('.shopify-payment-button');
        this.inputs = Array.from(this.querySelectorAll('[data-form-pdp-option]'));

        this.strings = {
          addToCart: this.dataset.stringAddToCart,
          soldOut: this.dataset.stringSoldOut,
          unavailable: this.dataset.stringUnavailable,
        };

        this.onOptionChange = this.onOptionChange.bind(this);
        this.inputs.forEach((input) => input.addEventListener('change', this.onOptionChange));

        this.setupStickyBar();
        this.updateAvailability();
      }

      disconnectedCallback() {
        this.inputs.forEach((input) => input.removeEventListener('change', this.onOptionChange));
        if (this.observer) this.observer.disconnect();
        this.mediaRequest?.abort();
      }

      /* Selected value for each option position, in order. */
      get selectedOptions() {
        const values = [];
        this.inputs.forEach((input) => {
          if (input.checked) values[Number(input.dataset.optionPosition) - 1] = input.value;
        });
        return values;
      }

      /* Option value ids for the checked chips, in the order Shopify's
         section rendering expects them. */
      get selectedOptionValueIds() {
        return this.inputs
          .filter((input) => input.checked)
          .map((input) => input.dataset.optionValueId)
          .filter(Boolean);
      }

      variantFor(options) {
        return this.variants.find((variant) =>
          options.every((value, index) => value === undefined || variant.options[index] === value)
        );
      }

      onOptionChange() {
        const variant = this.variantFor(this.selectedOptions);

        this.updateAvailability();
        this.updatePrice(variant);
        this.updateButtons(variant);
        this.updateUrl(variant);
        this.updateMedia(variant);
      }

      /* Strike through any chip that cannot be reached from the other
         currently-selected options. */
      updateAvailability() {
        const selected = this.selectedOptions;

        this.inputs.forEach((input) => {
          const position = Number(input.dataset.optionPosition) - 1;
          const probe = selected.slice();
          probe[position] = input.value;

          const match = this.variantFor(probe);
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !match || !match.available);
        });
      }

      updatePrice(variant) {
        if (!variant) return;

        if (this.priceTarget && variant.price_html) this.priceTarget.innerHTML = variant.price_html;
        if (this.stickyPrice && variant.price_html) this.stickyPrice.innerHTML = variant.price_html;
      }

      updateButtons(variant) {
        if (this.idInput && variant) this.idInput.value = variant.id;

        const purchasable = Boolean(variant && variant.available);
        let label = this.strings.addToCart;
        if (!variant) label = this.strings.unavailable;
        else if (!variant.available) label = this.strings.soldOut;

        this.submitButtons.forEach((button) => {
          button.disabled = !purchasable;
          button.setAttribute('aria-disabled', String(!purchasable));
          const text = button.querySelector('[data-form-pdp-submit-text]');
          if (text) text.textContent = label;
        });

        if (this.paymentButton) this.paymentButton.hidden = !purchasable;
      }

      updateUrl(variant) {
        if (!variant || this.dataset.updateUrl !== 'true') return;

        window.history.replaceState({}, '', `${this.dataset.productUrl}?variant=${variant.id}`);
      }

      /* The gallery is Dawn's, rendered server-side, so a colour change
         re-renders the section and swaps in the new markup — the same thing
         Dawn's product-info.js does. That keeps the whole media pipeline
         (which images the colour shows, slider, thumbnails, lightbox) in
         Liquid, with no second implementation to drift out of step. */
      updateMedia(variant) {
        const mediaWrapper = this.querySelector('.product__media-wrapper');
        if (!mediaWrapper) return;

        // Section renders pick up the selected variant from option_values,
        // the same params Dawn's product-info.js sends. A plain ?variant= is
        // ignored, which renders the section against the default variant and
        // filters the gallery for the wrong colour.
        const params = [`section_id=${this.dataset.sectionId}`];
        const optionValueIds = this.selectedOptionValueIds;

        if (optionValueIds.length) params.push(`option_values=${optionValueIds.join(',')}`);
        else if (variant) params.push(`variant=${variant.id}`);
        else return;

        this.mediaRequest?.abort();
        this.mediaRequest = new AbortController();

        const url = `${this.dataset.productUrl}?${params.join('&')}`;

        fetch(url, { signal: this.mediaRequest.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');

            const newMediaWrapper = html.querySelector('.product__media-wrapper');
            if (newMediaWrapper) mediaWrapper.replaceWith(newMediaWrapper);

            // the modal moves itself to <body> on first load, so update in place
            const modalContent = document.querySelector(
              `#ProductModal-${this.dataset.sectionId} .product-media-modal__content`
            );
            const newModalContent = html.querySelector('product-modal .product-media-modal__content');
            if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;

            if (this.dataset.zoom === 'hover' && typeof enableZoomOnHover === 'function') enableZoomOnHover(2);
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          });
      }

      setupStickyBar() {
        const bar = this.querySelector('[data-form-pdp-sticky]');
        const anchor = this.querySelector('[data-form-pdp-actions]');
        if (!bar || !anchor) return;

        if (!('IntersectionObserver' in window)) {
          bar.classList.add('is-visible');
          return;
        }

        this.observer = new IntersectionObserver(
          ([entry]) => bar.classList.toggle('is-visible', !entry.isIntersecting),
          { rootMargin: '0px 0px -120px 0px' }
        );
        this.observer.observe(anchor);
      }
    }
  );
}
